/** @jsx jsx */
import { jsx } from '@emotion/core';
import classnames from 'classnames/bind';
import { useEffect, useState } from 'react';
import serverRenderingUtils from '../../../helpers/serverRenderingUtils';
import { Icon } from '../../shared/Icon';
import Text from '../../shared/Text';
import styles from './styles.sass';
import {
  FieldInputPresentationComponentProps,
  FieldInputProps,
  FieldInputWithReduxProps,
} from './types';

const cx = classnames.bind(styles);

const isMobile =
  serverRenderingUtils.isIOS() || serverRenderingUtils.isAndroidChrome();

const FieldInputPresentationComponent = ({
  name,
  type,
  onChange,
  onBlur,
  onFocus,
  onClear,
  placeholder,
  value,
  min,
  max,
  autofocus,
  maxCharacterLength,
  isShowingError,
  error,
  iconType,
  cssProp,
  showCharacterCount,
  isDisabled,
}: FieldInputPresentationComponentProps) => {
  const isShowingBottomSection = showCharacterCount || isShowingError;
  const showClearButton = !!onClear && value !== '';
  return (
    <div css={cssProp}>
      <div className={styles.inputContainer}>
        {iconType && (
          <div className={styles.iconWrapper}>
            <Icon type={iconType} fillColor="#dedfe0" />
          </div>
        )}
        <input
          className={cx({
            input: true,
            numberInput: type === 'number',
            numberInputMobile: isMobile,
            inputError: isShowingError,
            inputWithClearButton: onClear,
            inputWithIcon: iconType,
            disabled: isDisabled,
          })}
          name={name}
          id={name}
          data-cy={name}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          value={value}
          type={type}
          min={type === 'number' ? min : undefined}
          max={type === 'number' ? max : undefined}
          maxLength={maxCharacterLength}
          autoFocus={autofocus}
        />
        {showClearButton && (
          <button
            type="button"
            onClick={onClear}
            name="Clear input"
            className={styles.clearButton}
          >
            <Icon type="x" fillColor="#fff" />
          </button>
        )}
      </div>
      {isShowingBottomSection && (
        <div className={styles.bottomSection}>
          <div
            className={cx({
              errorText: true,
              errorTextVisible: isShowingError,
            })}
          >
            <Text color="#d0021b">{error}</Text>
          </div>
          {showCharacterCount && (
            <Text size="sm" color="#7f8287">
              {getCharacterCountText({ maxCharacterLength, value })}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

function getCharacterCountText({
  maxCharacterLength,
  value,
}: Pick<FieldInputProps, 'maxCharacterLength' | 'value'>) {
  const displayValue = value === undefined ? 0 : value.length;
  return maxCharacterLength !== undefined
    ? `${displayValue} / ${maxCharacterLength}`
    : displayValue;
}

const FieldInput = ({
  name,
  type,
  onChange,
  onBlur,
  onFocus,
  onClear,
  placeholder,
  defaultValue,
  min,
  max,
  autofocus,
  maxCharacterLength,
  showCharacterCount,
  error,
  iconType,
  isShowingError,
  cssProp,
  isDisabled,
}: FieldInputProps) => {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (defaultValue !== undefined) setValue(defaultValue);
  }, [defaultValue]);
  return (
    <FieldInputPresentationComponent
      cssProp={cssProp}
      name={name}
      type={type}
      onChange={e => {
        setValue(e.currentTarget.value);
        onChange(e);
      }}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={placeholder}
      value={value}
      min={min}
      max={max}
      autofocus={autofocus}
      maxCharacterLength={maxCharacterLength}
      showCharacterCount={showCharacterCount}
      isShowingError={isShowingError}
      error={error}
      iconType={iconType}
      onClear={
        onClear !== undefined
          ? () => {
              setValue('');
              onClear();
            }
          : undefined
      }
      isDisabled={isDisabled}
    />
  );
};

const FieldInputWithRedux = ({
  input: { name, onChange, value, onBlur, onFocus },
  meta: { error, valid, submitFailed },
  type,
  placeholder,
  maxCharacterLength,
  min,
  max,
  autofocus,
  cssProp,
  isDisabled,
  showCharacterCount,
}: FieldInputWithReduxProps) => (
  <FieldInputPresentationComponent
    cssProp={cssProp}
    name={name}
    type={type}
    onChange={onChange}
    onBlur={onBlur}
    onFocus={onFocus}
    placeholder={placeholder}
    value={value}
    min={min}
    max={max}
    autofocus={autofocus}
    showCharacterCount={showCharacterCount}
    maxCharacterLength={maxCharacterLength}
    isShowingError={submitFailed && !valid}
    error={error}
    isDisabled={isDisabled}
  />
);

export { FieldInput, FieldInputWithRedux };
